/**
 * @fileoverview The flag engine every binding resolves through.
 * @module lib/features/core/flagRegistry
 *
 * `FlowGrid.init()` is one way to get flags. It is not the only one: an app may
 * run its own flag backend, evaluate on the server and seed the answers into the
 * page, or gate on flags in a runtime where the analytics SDK makes no sense.
 * Before this module those apps had to build the wiring themselves — a context,
 * a hook, a subscription — which is a reimplementation of the client we ship,
 * written by someone who cannot test it against our engine.
 *
 * So the SDK owns the wiring and the app owns only its answers. Register an
 * engine once and **every** reader sees it — `flags.on()` and React's
 * `useFlag` — with no provider component, no context, and nothing threaded
 * through the tree.
 *
 * ```ts
 * import { setFlagEngine, createFlagSource } from 'flowgrid-sdk';
 *
 * setFlagEngine(createFlagSource({ endpoint: '/api/my-flags' }));
 * // anywhere, any framework, from now on:
 * //   flags.on('new-checkout')      useFlag('new-checkout')      flag('new-checkout')
 * ```
 *
 * Resolution order, and why:
 *
 *   1. **An explicitly passed engine** — a test stub, a second instance. The
 *      caller was specific; nothing global should override that.
 *   2. **A registered engine** — this module. An app that registered one meant
 *      it to win over an incidental `FlowGrid.init()` elsewhere on the page.
 *   3. **The active SDK instance** — the `FlowGrid.init()` default.
 *
 * Like `resolve.ts`, the registration is mirrored on a page global. A binding
 * bundled by the app is a *different module instance* from one loaded by a
 * script tag, so a module-scoped variable alone silently fails to cross that
 * boundary — the exact bug `resolve.ts` documents for `window.FlowGrid`.
 */

import { activeInstance } from './resolve';
import { createFlagSource, readSeededFlags, type FlagSource } from './flagSource';
import type { FlagVariantValue } from './flagEval';

/**
 * The minimum an engine must do to drive a binding.
 *
 * Deliberately structural and tiny: an app registering its own source should not
 * have to subclass anything, and `FeatureFlags` satisfies this by construction.
 */
export interface FlagEngineLike {
  on(key: string, fallback?: boolean): boolean;
  ready(): Promise<void>;
  onChange(listener: () => void): () => void;
  readonly isReady: boolean;
  /**
   * Optional, and optional on purpose. An app that registered its own engine
   * before variants existed still satisfies this interface, and a binding that
   * asks for an arm from an engine that has none gets `null` rather than a
   * crash — see `variantFrom`/`valueFrom` below.
   *
   * Requiring them would have made this a breaking change to the one contract
   * whose whole point is that an app can satisfy it without subclassing.
   */
  variant?(key: string): string | null;
  get?<T extends FlagVariantValue>(key: string, fallback: T): T;
  /** Optional for the same reason: an engine we do not own may not report. */
  reportError?(key: string, error: unknown): void;
}

/** Report through an engine that may not report. Never throws. */
export function reportFrom(engine: FlagEngineLike | null | undefined, key: string, error: unknown): void {
  try {
    engine?.reportError?.(key, error);
  } catch {
    /* reporting must never break a render */
  }
}

/**
 * Read an arm from an engine that may not do arms.
 *
 * Every binding goes through these two rather than calling the optional methods
 * directly, so "an older engine has no variants" is handled once instead of at
 * each of the four framework bindings — where three of them would eventually
 * get it right and the fourth would throw in somebody's production render.
 */
export function variantFrom(engine: FlagEngineLike | null | undefined, key: string): string | null {
  try {
    return engine?.variant?.(key) ?? null;
  } catch {
    return null;
  }
}

/** Read a variant's value from an engine that may not do arms. */
export function valueFrom<T extends FlagVariantValue>(
  engine: FlagEngineLike | null | undefined,
  key: string,
  fallback: T,
): T {
  try {
    const read = engine?.get?.(key, fallback);
    return read === undefined ? fallback : read;
  } catch {
    return fallback;
  }
}

const GLOBAL_KEY = '__flowgridFlagEngine';

interface FlagEngineGlobal {
  [GLOBAL_KEY]?: FlagEngineLike | null;
}

/** Module-scoped copy, for the common case where there is only one bundle. */
let registered: FlagEngineLike | null = null;

function globalScope(): FlagEngineGlobal | null {
  if (typeof globalThis === 'undefined') return null;
  return globalThis as unknown as FlagEngineGlobal;
}

/**
 * Make this engine the one every binding reads from.
 *
 * Idempotent, and safe to call before or after `FlowGrid.init()`. Pass `null` to
 * unregister — worth doing between tests, so one suite's stub cannot leak into
 * the next.
 */
export function setFlagEngine(engine: FlagEngineLike | null): void {
  registered = engine;
  const scope = globalScope();
  if (scope) {
    try {
      scope[GLOBAL_KEY] = engine;
    } catch {
      /* frozen or exotic global — the module-scoped copy still works */
    }
  }
}

/** The registered engine, if an app set one. */
export function getFlagEngine(): FlagEngineLike | null {
  if (registered) return registered;
  const scope = globalScope();
  if (!scope) return null;
  try {
    return scope[GLOBAL_KEY] ?? null;
  } catch {
    return null;
  }
}

/** Where a seeded page may name its refresh endpoint. */
const ENDPOINT_KEY = '__FLOWGRID_FLAGS_ENDPOINT__';

/**
 * Adopt answers a server rendered into the page, if it did and nobody has
 * registered anything better.
 *
 * This is what lets an app have server-evaluated flags with **no** client
 * bootstrap at all: the server writes `window.__FLOWGRID_FLAGS__` (and
 * optionally `window.__FLOWGRID_FLAGS_ENDPOINT__` to refresh from), and the
 * first flag read anywhere — `flags.on()`, `useFlag()`, `flag()` — finds them.
 *
 * The alternative was every app shipping a provider component whose entire job
 * was to move that object into the SDK. That component is the wheel; this is the
 * SDK owning it. It also means a template with no bundler gets the same
 * behaviour as a React app, because a `<script>` writing a global is the one
 * mechanism every server framework already has.
 */
function adoptSeed(): FlagSource | null {
  const seed = readSeededFlags();
  if (!seed) return null;

  const scope = globalScope() as (FlagEngineGlobal & Record<string, unknown>) | null;
  const endpoint = typeof scope?.[ENDPOINT_KEY] === 'string'
    ? (scope[ENDPOINT_KEY] as string)
    : undefined;

  const source = createFlagSource({ initial: seed, endpoint });
  setFlagEngine(source);
  return source;
}

/**
 * The engine a binding should use: explicit, else registered, else a
 * server-rendered seed, else the live SDK.
 *
 * Never throws and never returns a half-built object — a binding must degrade to
 * "no engine" (and therefore to the caller's fallback), not to a broken render.
 */
export function resolveFlagEngine(explicit?: FlagEngineLike | null): FlagEngineLike | null {
  if (explicit) return explicit;

  const own = getFlagEngine();
  if (own) return own;

  try {
    const seeded = adoptSeed();
    if (seeded) return seeded;
  } catch {
    /* a malformed seed must not break a flag read */
  }

  try {
    return (activeInstance()?.flags as FlagEngineLike | undefined) ?? null;
  } catch {
    return null;
  }
}
